import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add,
  MoreHoriz,
  Search,
  PersonOutline,
  MailOutline,
  PhoneOutlined,
  CalendarTodayOutlined,
  AdminPanelSettingsOutlined,
} from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import moment from 'moment';

import CreateUserModal from './CreateUserModal';
import UpdateUser from './UpdateUser/UpdateUser';
import { getPendingUsers, getUsers } from '../../../helpers/api';
import { properCase } from '../../../helpers/utils';
import { openNotification } from '../../../helpers/notification';
import { AppContext } from '../../../store/AppContext';
import UserContextProvider from '../../../store/UserContext';
import LoaderSkeleton from '../../common/LoaderSkeleton';

const Users = ({ users, refetchUsersData, props, history }) => {
  const [loading, setLoading] = useState(false);
  const [visible, setvisible] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [searchResults, setSearchResults] = useState(users);
  const [updateModalVisible, setupdateModalVisible] = useState(false);

  const [selectedUserId, setselectedUserId] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 for Active, 1 for Pending
  const [anchorEl, setAnchorEl] = useState(null);

  const { state } = useContext(AppContext);
  const { userPermission } = state.auth.result.data;

  const userPerm =
    userPermission?.flatMap((permission) =>
      permission.permissionActivity?.map((activity) => activity.privilegeCode.toLowerCase())
    ) || [];

  const canCreateUser = userPerm.includes('user_invite');

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  useEffect(() => {
    setSearchResults(users);
  }, [users]);

  const fetchPendingUsers = async () => {
    try {
      const res = await getPendingUsers();
      setPendingUsers(res.result.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleActionClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setselectedUserId(id);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
  };

  const handleShowProfile = () => {
    handleActionClose();
    setupdateModalVisible(true);
  };

  const searchUsersTable = (searchValue) => {
    setLoading(true);
    const params = `?search=${searchValue}&Start=0&Stop=100000`;
    getUsers(params)
      .then((res) => setSearchResults(res.result.data))
      .catch(() => setSearchResults([]))
      .finally(() => setLoading(false));
  };

  // Logic for the Profile Modal props
  const selectedUserData = users.filter((user) => user.userId === selectedUserId);
  const userProfileProps = selectedUserData[0] || {};

  const activeColumns = [
    {
      name: 'userCode',
      label: 'Reg No',
      options: {
        customHeadLabelRender: (a) => (
          <Typography variant="button" fontWeight={700} color="text.secondary">
            {a.label}
          </Typography>
        ),
        customBodyRender: (val) => (
          <Typography variant="body2" fontWeight={600}>
            {val}
          </Typography>
        ),
      },
    },
    {
      name: 'fullName',
      label: 'Full Name',
      options: {
        customHeadLabelRender: (a) => (
          <Typography variant="button" fontWeight={700} color="text.secondary">
            {a.label}
          </Typography>
        ),
        customBodyRender: (val) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonOutline fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={500}>
              {val}
            </Typography>
          </Stack>
        ),
      },
    },
    {
      name: 'email',
      label: 'Email Address',
      options: {
        customHeadLabelRender: (a) => (
          <Typography variant="button" fontWeight={700} color="text.secondary">
            {a.label}
          </Typography>
        ),
        customBodyRender: (val) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <MailOutline fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="body2">{val}</Typography>
          </Stack>
        ),
      },
    },
    {
      name: 'userCategory',
      label: 'Category',
      options: {
        customHeadLabelRender: (a) => (
          <Typography variant="button" fontWeight={700} color="text.secondary">
            {a.label}
          </Typography>
        ),
        customBodyRender: (val) => (
          <Chip
            label={val}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
        ),
      },
    },
    {
      name: 'role',
      label: 'Role',
      options: {
        customHeadLabelRender: (a) => (
          <Typography variant="button" fontWeight={700} color="text.secondary">
            {a.label}
          </Typography>
        ),
        customBodyRender: (val) => (
          <Typography variant="body2" sx={{ textTransform: 'lowercase' }}>
            {val}
          </Typography>
        ),
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customHeadLabelRender: (a) => (
          <Typography variant="button" fontWeight={700} color="text.secondary">
            {a.label}
          </Typography>
        ),
        customBodyRender: (val) => {
          const isActive = val === 'ACTIVE';
          return (
            <Chip
              label={properCase(val)}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.65rem',
                bgcolor: isActive ? alpha('#2e7d32', 0.1) : alpha('#ed6c02', 0.1),
                color: isActive ? '#2e7d32' : '#ed6c02',
                borderRadius: '6px',
              }}
            />
          );
        },
      },
    },
    {
      name: 'userId',
      label: ' ',
      options: {
        customBodyRender: (val) => (
          <IconButton size="small" onClick={(e) => handleActionClick(e, val)}>
            <MoreHoriz />
          </IconButton>
        ),
      },
    },
  ];

  const pendingColumns = [
    { name: 'email', label: 'Email Address' },
    {
      name: 'entryDate',
      label: 'Date Created',
      options: { customBodyRender: (val) => moment(val).format('Do-MMM-YYYY') },
    },
    { name: 'userCategory', label: 'Category' },
    { name: 'role', label: 'Role' },
    {
      name: 'inviteStatus',
      label: 'Status',
      options: {
        customBodyRender: (val) => (
          <Chip
            label={val?.toLowerCase()}
            size="small"
            sx={{ bgcolor: alpha('#ed6c02', 0.1), color: '#ed6c02', fontWeight: 700 }}
          />
        ),
      },
    },
  ];

  const tableOptions = {
    elevation: 0,
    selectableRows: 'none',
    responsive: 'standard',
    search: false,
    download: false,
    print: false,
    filter: false,
    viewColumns: false,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50],
    setRowProps: (ev, dataIndex) => ({
      style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
    }),
    setTableProps: () => ({
      size: 'small',
      sx: {
        // Removes the massive 64px toolbar
        '& .MUIDataTableToolbar-root': {
          display: 'none',
        },
        // Adds a precise, small padding to the top of the header cells
        '& .MuiTableCell-head': {
          paddingTop: '16px',
          paddingBottom: '16px',
        },
      },
    }),
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F4F6F8', minHeight: '100vh', mt: -1 }}>
      <UserContextProvider>
        <CreateUserModal
          open={visible}
          closeCreateUserModal={() => setvisible(false)}
          refetch={refetchUsersData}
          close={() => setvisible(false)}
        />
        {updateModalVisible && (
          <UpdateUser
            open={updateModalVisible}
            close={() => setupdateModalVisible(false)}
            name={userProfileProps.username}
            userId={selectedUserId}
            email={userProfileProps.email}
            userCategory={userProfileProps.userCategory}
            areaPermit={userProfileProps.userAreaPermissions}
            users={users}
            userData={selectedUserData}
          />
        )}
      </UserContextProvider>

      {/* HEADER SECTION */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Invite and manage administrative and facility users.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              size="small"
              placeholder="Search users..."
              onChange={(e) => searchUsersTable(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '10px', bgcolor: '#fff', width: 220 },
              }}
            />
            {canCreateUser && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Add />}
                onClick={() => props.history.push('users/invite')}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3 }}
              >
                New User
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* TABS & TABLE AREA */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ px: 2, pt: 1 }}>
          <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)}>
            <Tab label="Active Users" sx={{ textTransform: 'none', fontWeight: 700 }} />
            <Tab label="Pending Invites" sx={{ textTransform: 'none', fontWeight: 700 }} />
          </Tabs>
        </Box>
        <Divider />
        <Box>
          {loading ? (
            <Box sx={{ p: 4 }}>
              <LoaderSkeleton />
              <Divider sx={{ my: 3 }} />
              <LoaderSkeleton />
            </Box>
          ) : tabValue === 0 ? (
            <MUIDataTable
              title=""
              data={searchResults || []}
              columns={activeColumns}
              options={tableOptions}
            />
          ) : (
            <MUIDataTable
              title=""
              data={pendingUsers || []}
              columns={pendingColumns}
              options={tableOptions}
            />
          )}
        </Box>
      </Paper>

      {/* ACTIONS MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: 150,
            mt: 1,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        <MenuItem onClick={handleShowProfile} sx={{ gap: 2 }}>
          <AdminPanelSettingsOutlined fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600}>
            User Profile
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Users;
